import React from 'react';

import styles from '../LocalAdminConsole.module.scss';
import type { LocalConsoleAccessGrant } from '../localConsoleAccess';
import type { LocalConsoleContext } from '../types';

type Field = { label: string; value: string; testId: string };

const ACCESS_LABELS: Record<LocalConsoleAccessGrant, string> = {
  'offline-mode-admin': 'Offline Mode admin (offline_mode_activation)',
  'super-admin': 'TigerConnect super admin',
  'org-admin-grandfathered': 'Organization admin (grandfathered)',
  none: 'No Offline Mode permission',
};

export default function LocalConsoleOverview({
  userDisplayName,
  userId,
  organizationName,
  organizationId,
  isLocalServer,
  apiBaseUrl,
  deploymentMode,
  appVersion,
  access,
}: LocalConsoleContext) {
  const sessionFields: Field[] = [
    { label: 'Signed in as', value: userDisplayName || '—', testId: 'localConsoleUserName' },
    { label: 'User ID', value: userId || '—', testId: 'localConsoleUserId' },
    {
      label: 'Offline Mode access',
      value: ACCESS_LABELS[access.grantedBy],
      testId: 'localConsoleAccessGrant',
    },
    {
      label: 'Organization',
      value: organizationName || '—',
      testId: 'localConsoleOrganizationName',
    },
    {
      label: 'Organization ID',
      value: organizationId || '—',
      testId: 'localConsoleOrganizationId',
    },
  ];

  const deploymentFields: Field[] = [
    {
      label: 'Server',
      value: isLocalServer ? 'Local server (stand-in)' : 'Cloud',
      testId: 'localConsoleServerMode',
    },
    { label: 'API endpoint', value: apiBaseUrl || '—', testId: 'localConsoleApiBaseUrl' },
    { label: 'Endpoint resolved as', value: deploymentMode, testId: 'localConsoleDeploymentMode' },
    { label: 'App version', value: appVersion || '—', testId: 'localConsoleAppVersion' },
  ];

  const renderField = ({ label, value, testId }: Field) => (
    <div className={styles.field} key={testId}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue} data-test-id={testId}>
        {value}
      </span>
    </div>
  );

  return (
    <div data-test-id="localConsoleOverview">
      <p className={styles.sectionIntro}>
        This console is served by the local server and talks only to it. Every section here renders
        without contacting TigerConnect&apos;s cloud.
      </p>

      <section className={styles.section}>
        <h3 className={styles.sectionHeader}>Session</h3>
        <div className={styles.fieldGrid}>{sessionFields.map(renderField)}</div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionHeader}>Deployment</h3>
        <div className={styles.fieldGrid}>{deploymentFields.map(renderField)}</div>
      </section>
    </div>
  );
}
